import { useCallback, useContext, useMemo } from 'react';
import { DateTime } from 'luxon';
import PeriodsContext from 'contexts/PeriodsContext';
import captureException from 'services/captureException';
import moment from 'moment';

const useSlideoutDateScope = (
  slideoutDateRange: PerformanceBoardTypes.SlideOut.SlideOutDateRange,
  dateField?: string,
) => {
  const { selectedPeriod, periods } = useContext(PeriodsContext);

  if (!selectedPeriod) {
    const e = new Error();
    e.name = 'useSlideOutDateScope: No period selected';
    throw e;
  }

  const today = useMemo(() => {
    return DateTime.local().toISO();
  }, []);

  const isSelectedPeriodInProgress = useMemo(() => {
    return selectedPeriod.startDate < today && today < selectedPeriod.endDate;
  }, [selectedPeriod.endDate, selectedPeriod.startDate, today]);

  const getCurrentIntervalDateScope = useCallback((): DateRangeInput => {
    if (isSelectedPeriodInProgress) {
      return {
        dateField,
        startDate: selectedPeriod.startDate,
        endDate: DateTime.fromISO(today).toISODate(),
      };
    }
    return {
      dateField,
      startDate: selectedPeriod.startDate,
      endDate: selectedPeriod.endDate,
    };
  }, [
    dateField,
    isSelectedPeriodInProgress,
    selectedPeriod.endDate,
    selectedPeriod.startDate,
    today,
  ]);

  const getLastIntervalDateScope = useCallback(():
    | DateRangeInput
    | undefined => {
    if (slideoutDateRange.mode !== 'Last') {
      const e = new Error();
      e.name =
        'useSlideOutDateScope: Cannot generate date scope for non-last mode';
      captureException(e);
      return undefined;
    }

    const periodsAsc = periods.sort((a, b) => {
      if (a.startDate > b.startDate) {
        return -1;
      } else if (a.startDate < b.startDate) {
        return 1;
      } else {
        return 0;
      }
    });
    const indexOfSelectedPeriod = periodsAsc.findIndex(
      (p) => p.startDate === selectedPeriod.startDate,
    );
    const relevantPeriods = periodsAsc.slice(
      indexOfSelectedPeriod + 1,
      indexOfSelectedPeriod + slideoutDateRange.n + 1,
    );

    if (relevantPeriods.length === 0) {
      const e = new Error();
      e.name = 'useSlideOutDateScope: No relevant periods found';
      captureException(e);
      return undefined;
    }

    if (slideoutDateRange.isToDate) {
      const today = moment().format('YYYY-MM-DD');
      if (
        today >= selectedPeriod.startDate &&
        today <= selectedPeriod.endDate
      ) {
        return {
          startDate: relevantPeriods[relevantPeriods.length - 1].startDate,
          endDate: today,
          dateField,
        };
      } else {
        return {
          startDate: relevantPeriods[relevantPeriods.length - 1].startDate,
          endDate: selectedPeriod.endDate,
          dateField,
        };
      }
    }

    return {
      startDate: relevantPeriods[relevantPeriods.length - 1].startDate,
      endDate: relevantPeriods[0].endDate,
      dateField,
    };
  }, [
    dateField,
    periods,
    selectedPeriod.endDate,
    selectedPeriod.startDate,
    slideoutDateRange,
  ]);

  const dateScope = useMemo((): DateRangeInput | undefined => {
    if (slideoutDateRange.mode === 'Current Interval') {
      return getCurrentIntervalDateScope();
    }

    return getLastIntervalDateScope();
  }, [
    getCurrentIntervalDateScope,
    getLastIntervalDateScope,
    slideoutDateRange,
  ]);

  return dateScope;
};

export default useSlideoutDateScope;
